import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { showBannerAd, hideBannerAd, isNativeMobile } from '../utils/adMobService';
import { useAdFreeSubscriber } from '../contexts/AdFreeSubscriberContext';

/** Exact routes where the banner should NOT appear. */
const NO_AD_EXACT = new Set([
  '/',
  '/home',
  '/onboarding',
  '/login',
  '/register',
  '/forgot-password',
  '/reset-password',
  '/subscription',
]);

/** Route prefixes where the banner should NOT appear (dynamic routes). */
const NO_AD_PREFIXES = ['/quiz/', '/paid-book/'];

function shouldHideAd(pathname: string): boolean {
  if (NO_AD_EXACT.has(pathname)) return true;
  return NO_AD_PREFIXES.some(prefix => pathname.startsWith(prefix));
}

const BANNER_VISIBLE_CLASS = 'ad-banner-visible';

/**
 * Global banner ad manager for native Android/iOS.
 * Lives inside the Router so it can watch route changes via useLocation().
 * Shows/hides a single top-pinned AdMob banner based on the current route —
 * one operation per navigation, preventing the rapid mount/unmount cycles
 * that cause crashes when each page manages its own banner.
 *
 * Web AdSense is handled inline by each page's <LessonBannerAd /> component.
 */
const BannerAdManager: React.FC = () => {
  const location = useLocation();
  const bannerVisible = useRef(false);
  const { isPremiumSubscriber } = useAdFreeSubscriber();

  useEffect(() => {
    if (!isNativeMobile()) return;

    const shouldShow = !isPremiumSubscriber && !shouldHideAd(location.pathname);

    if (shouldShow && !bannerVisible.current) {
      bannerVisible.current = true;
      document.body.classList.add(BANNER_VISIBLE_CLASS);
      showBannerAd();
    } else if (!shouldShow && bannerVisible.current) {
      bannerVisible.current = false;
      document.body.classList.remove(BANNER_VISIBLE_CLASS);
      hideBannerAd();
    }
    // If shouldShow===true and banner is already visible, do nothing — no flicker.
  }, [location.pathname, isPremiumSubscriber]);

  // Hide banner when component unmounts (app closing)
  useEffect(() => {
    return () => {
      if (isNativeMobile() && bannerVisible.current) {
        hideBannerAd();
      }
      document.body.classList.remove(BANNER_VISIBLE_CLASS);
    };
  }, []);

  return null; // No UI — purely side-effects
};

export default BannerAdManager;
